import React from "react";
import { Box, Container, Typography, Link as MLink } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import CivixButton from "../components/ui/CivixButton";
import { FONT_PRIMARY, FONT_WEIGHT } from "../theme/fonts";

const SUPPORT_EMAIL = "support@civixthread.com";

export default function SupportPage() {
  return (
    <Box
      component="main"
      sx={{
        bgcolor: "var(--root-background, #f8f9fa)",
        backgroundImage:
          "radial-gradient(900px circle at 18% -12%, var(--secondary-bg-color-light, rgba(4,120,87,0.15)), transparent 55%)",
        py: { xs: 5, md: 8 },
        px: { xs: 2, md: 3 },
      }}
    >
      <Container maxWidth="sm" disableGutters>
        <Box sx={{ textAlign: "center", mb: { xs: 3, md: 4 } }}>
          <Typography
            component="h1"
            sx={{
              fontFamily: FONT_PRIMARY,
              fontWeight: FONT_WEIGHT.extraBold,
              fontSize: { xs: 28, md: 40 },
              letterSpacing: "-0.02em",
              lineHeight: 1.15,
              color: "var(--black-color)",
              mb: 1.5,
            }}
          >
            How can we help?
          </Typography>
          <Typography
            sx={{
              fontFamily: FONT_PRIMARY,
              fontSize: { xs: 15, md: 17 },
              lineHeight: 1.65,
              color: "var(--dna-color-palette-black-400)",
            }}
          >
            Questions about your account, verification, or using CivixThread — our team is here.
          </Typography>
        </Box>

        <Box
          sx={{
            bgcolor: "var(--join-surface-elevated, #ffffff)",
            border: "1px solid var(--join-border-muted, #e4e4e4)",
            borderRadius: "var(--radius-card, 12px)",
            boxShadow: "var(--join-shadow-elevated)",
            p: { xs: 3, md: 4 },
            textAlign: "center",
          }}
        >
          <Typography
            component="h2"
            sx={{
              fontFamily: FONT_PRIMARY,
              fontWeight: FONT_WEIGHT.extraBold,
              fontSize: { xs: 20, md: 24 },
              color: "var(--black-color)",
              mb: 1,
            }}
          >
            Contact support
          </Typography>
          <Typography
            sx={{
              fontFamily: FONT_PRIMARY,
              fontSize: { xs: 15, md: 16 },
              lineHeight: 1.65,
              color: "var(--dna-color-palette-black-400)",
              maxWidth: 440,
              mx: "auto",
              mb: 3,
            }}
          >
            Email us and include your account email plus a short description of the issue so we can
            help faster.
          </Typography>

          <CivixButton
            component="a"
            href={`mailto:${SUPPORT_EMAIL}`}
            size="md"
            variant="pill"
            sx={{
              mb: 2,
              boxShadow: "none",
              "&:hover": {
                boxShadow: "none",
                bgcolor: "var(--primary-color-hover, #065f46)",
              },
            }}
          >
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
              <EmailOutlinedIcon sx={{ fontSize: 20 }} aria-hidden />
              {SUPPORT_EMAIL}
            </Box>
          </CivixButton>

          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.75,
              color: "var(--dna-color-palette-black-400)",
              fontSize: 14,
              fontFamily: FONT_PRIMARY,
            }}
          >
            <ScheduleOutlinedIcon sx={{ fontSize: 18 }} aria-hidden />
            We aim to respond within 24–48 hours.
          </Box>

          <Box
            sx={{
              mt: 3.5,
              pt: 2.5,
              borderTop: "1px solid var(--join-border-muted, #e4e4e4)",
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: { xs: 1.5, sm: 2.5 },
            }}
          >
            <MLink
              component={RouterLink}
              to="/features"
              underline="hover"
              sx={{
                color: "var(--primary-color-hover)",
                fontSize: 14,
                fontWeight: FONT_WEIGHT.semibold,
                fontFamily: FONT_PRIMARY,
              }}
            >
              Features
            </MLink>
            <MLink
              component={RouterLink}
              to="/privacy-policy"
              underline="hover"
              sx={{
                color: "var(--primary-color-hover)",
                fontSize: 14,
                fontWeight: FONT_WEIGHT.semibold,
                fontFamily: FONT_PRIMARY,
              }}
            >
              Privacy Policy
            </MLink>
            <MLink
              component={RouterLink}
              to="/terms"
              underline="hover"
              sx={{
                color: "var(--primary-color-hover)",
                fontSize: 14,
                fontWeight: FONT_WEIGHT.semibold,
                fontFamily: FONT_PRIMARY,
              }}
            >
              Terms of Service
            </MLink>
            <MLink
              component={RouterLink}
              to="/child-safety-standards"
              underline="hover"
              sx={{
                color: "var(--primary-color-hover)",
                fontSize: 14,
                fontWeight: FONT_WEIGHT.semibold,
                fontFamily: FONT_PRIMARY,
              }}
            >
              Child Safety Standards
            </MLink>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
